/**
 * Country lookup table and mapping helpers.
 * Includes ISO 3166-1 alpha-2 country names/continents plus UN M49 → alpha-2 mapping.
 */

interface CountryInfo {
  name: string;
  continent: string;
}

const COUNTRIES: Record<string, CountryInfo> = {
  AD: { name: "Andorra", continent: "Europe" },
  AE: { name: "United Arab Emirates", continent: "Asia" },
  AF: { name: "Afghanistan", continent: "Asia" },
  AG: { name: "Antigua and Barbuda", continent: "North America" },
  AI: { name: "Anguilla", continent: "North America" },
  AL: { name: "Albania", continent: "Europe" },
  AM: { name: "Armenia", continent: "Asia" },
  AO: { name: "Angola", continent: "Africa" },
  AQ: { name: "Antarctica", continent: "Antarctica" },
  AR: { name: "Argentina", continent: "South America" },
  AS: { name: "American Samoa", continent: "Oceania" },
  AT: { name: "Austria", continent: "Europe" },
  AU: { name: "Australia", continent: "Oceania" },
  AW: { name: "Aruba", continent: "North America" },
  AX: { name: "Åland Islands", continent: "Europe" },
  AZ: { name: "Azerbaijan", continent: "Asia" },
  BA: { name: "Bosnia and Herzegovina", continent: "Europe" },
  BB: { name: "Barbados", continent: "North America" },
  BD: { name: "Bangladesh", continent: "Asia" },
  BE: { name: "Belgium", continent: "Europe" },
  BF: { name: "Burkina Faso", continent: "Africa" },
  BG: { name: "Bulgaria", continent: "Europe" },
  BH: { name: "Bahrain", continent: "Asia" },
  BI: { name: "Burundi", continent: "Africa" },
  BJ: { name: "Benin", continent: "Africa" },
  BL: { name: "Saint Barthélemy", continent: "North America" },
  BM: { name: "Bermuda", continent: "North America" },
  BN: { name: "Brunei", continent: "Asia" },
  BO: { name: "Bolivia", continent: "South America" },
  BQ: { name: "Caribbean Netherlands", continent: "North America" },
  BR: { name: "Brazil", continent: "South America" },
  BS: { name: "Bahamas", continent: "North America" },
  BT: { name: "Bhutan", continent: "Asia" },
  BV: { name: "Bouvet Island", continent: "Antarctica" },
  BW: { name: "Botswana", continent: "Africa" },
  BY: { name: "Belarus", continent: "Europe" },
  BZ: { name: "Belize", continent: "North America" },
  CA: { name: "Canada", continent: "North America" },
  CC: { name: "Cocos (Keeling) Islands", continent: "Asia" },
  CD: { name: "DR Congo", continent: "Africa" },
  CF: { name: "Central African Republic", continent: "Africa" },
  CG: { name: "Congo", continent: "Africa" },
  CH: { name: "Switzerland", continent: "Europe" },
  CI: { name: "Côte d'Ivoire", continent: "Africa" },
  CK: { name: "Cook Islands", continent: "Oceania" },
  CL: { name: "Chile", continent: "South America" },
  CM: { name: "Cameroon", continent: "Africa" },
  CN: { name: "China", continent: "Asia" },
  CO: { name: "Colombia", continent: "South America" },
  CR: { name: "Costa Rica", continent: "North America" },
  CU: { name: "Cuba", continent: "North America" },
  CV: { name: "Cape Verde", continent: "Africa" },
  CW: { name: "Curaçao", continent: "North America" },
  CX: { name: "Christmas Island", continent: "Asia" },
  CY: { name: "Cyprus", continent: "Europe" },
  CZ: { name: "Czechia", continent: "Europe" },
  DE: { name: "Germany", continent: "Europe" },
  DJ: { name: "Djibouti", continent: "Africa" },
  DK: { name: "Denmark", continent: "Europe" },
  DM: { name: "Dominica", continent: "North America" },
  DO: { name: "Dominican Republic", continent: "North America" },
  DZ: { name: "Algeria", continent: "Africa" },
  EC: { name: "Ecuador", continent: "South America" },
  EE: { name: "Estonia", continent: "Europe" },
  EG: { name: "Egypt", continent: "Africa" },
  EH: { name: "Western Sahara", continent: "Africa" },
  ER: { name: "Eritrea", continent: "Africa" },
  ES: { name: "Spain", continent: "Europe" },
  ET: { name: "Ethiopia", continent: "Africa" },
  FI: { name: "Finland", continent: "Europe" },
  FJ: { name: "Fiji", continent: "Oceania" },
  FK: { name: "Falkland Islands", continent: "South America" },
  FM: { name: "Micronesia", continent: "Oceania" },
  FO: { name: "Faroe Islands", continent: "Europe" },
  FR: { name: "France", continent: "Europe" },
  GA: { name: "Gabon", continent: "Africa" },
  GB: { name: "United Kingdom", continent: "Europe" },
  GD: { name: "Grenada", continent: "North America" },
  GE: { name: "Georgia", continent: "Asia" },
  GF: { name: "French Guiana", continent: "South America" },
  GG: { name: "Guernsey", continent: "Europe" },
  GH: { name: "Ghana", continent: "Africa" },
  GI: { name: "Gibraltar", continent: "Europe" },
  GL: { name: "Greenland", continent: "North America" },
  GM: { name: "Gambia", continent: "Africa" },
  GN: { name: "Guinea", continent: "Africa" },
  GP: { name: "Guadeloupe", continent: "North America" },
  GQ: { name: "Equatorial Guinea", continent: "Africa" },
  GR: { name: "Greece", continent: "Europe" },
  GS: { name: "South Georgia and the South Sandwich Islands", continent: "Antarctica" },
  GT: { name: "Guatemala", continent: "North America" },
  GU: { name: "Guam", continent: "Oceania" },
  GW: { name: "Guinea-Bissau", continent: "Africa" },
  GY: { name: "Guyana", continent: "South America" },
  HK: { name: "Hong Kong", continent: "Asia" },
  HM: { name: "Heard Island and McDonald Islands", continent: "Antarctica" },
  HN: { name: "Honduras", continent: "North America" },
  HR: { name: "Croatia", continent: "Europe" },
  HT: { name: "Haiti", continent: "North America" },
  HU: { name: "Hungary", continent: "Europe" },
  ID: { name: "Indonesia", continent: "Asia" },
  IE: { name: "Ireland", continent: "Europe" },
  IL: { name: "Israel", continent: "Asia" },
  IM: { name: "Isle of Man", continent: "Europe" },
  IN: { name: "India", continent: "Asia" },
  IO: { name: "British Indian Ocean Territory", continent: "Asia" },
  IQ: { name: "Iraq", continent: "Asia" },
  IR: { name: "Iran", continent: "Asia" },
  IS: { name: "Iceland", continent: "Europe" },
  IT: { name: "Italy", continent: "Europe" },
  JE: { name: "Jersey", continent: "Europe" },
  JM: { name: "Jamaica", continent: "North America" },
  JO: { name: "Jordan", continent: "Asia" },
  JP: { name: "Japan", continent: "Asia" },
  KE: { name: "Kenya", continent: "Africa" },
  KG: { name: "Kyrgyzstan", continent: "Asia" },
  KH: { name: "Cambodia", continent: "Asia" },
  KI: { name: "Kiribati", continent: "Oceania" },
  KM: { name: "Comoros", continent: "Africa" },
  KN: { name: "Saint Kitts and Nevis", continent: "North America" },
  KP: { name: "North Korea", continent: "Asia" },
  KR: { name: "South Korea", continent: "Asia" },
  KW: { name: "Kuwait", continent: "Asia" },
  KY: { name: "Cayman Islands", continent: "North America" },
  KZ: { name: "Kazakhstan", continent: "Asia" },
  LA: { name: "Laos", continent: "Asia" },
  LB: { name: "Lebanon", continent: "Asia" },
  LC: { name: "Saint Lucia", continent: "North America" },
  LI: { name: "Liechtenstein", continent: "Europe" },
  LK: { name: "Sri Lanka", continent: "Asia" },
  LR: { name: "Liberia", continent: "Africa" },
  LS: { name: "Lesotho", continent: "Africa" },
  LT: { name: "Lithuania", continent: "Europe" },
  LU: { name: "Luxembourg", continent: "Europe" },
  LV: { name: "Latvia", continent: "Europe" },
  LY: { name: "Libya", continent: "Africa" },
  MA: { name: "Morocco", continent: "Africa" },
  MC: { name: "Monaco", continent: "Europe" },
  MD: { name: "Moldova", continent: "Europe" },
  ME: { name: "Montenegro", continent: "Europe" },
  MF: { name: "Saint Martin", continent: "North America" },
  MG: { name: "Madagascar", continent: "Africa" },
  MH: { name: "Marshall Islands", continent: "Oceania" },
  MK: { name: "North Macedonia", continent: "Europe" },
  ML: { name: "Mali", continent: "Africa" },
  MM: { name: "Myanmar", continent: "Asia" },
  MN: { name: "Mongolia", continent: "Asia" },
  MO: { name: "Macao", continent: "Asia" },
  MP: { name: "Northern Mariana Islands", continent: "Oceania" },
  MQ: { name: "Martinique", continent: "North America" },
  MR: { name: "Mauritania", continent: "Africa" },
  MS: { name: "Montserrat", continent: "North America" },
  MT: { name: "Malta", continent: "Europe" },
  MU: { name: "Mauritius", continent: "Africa" },
  MV: { name: "Maldives", continent: "Asia" },
  MW: { name: "Malawi", continent: "Africa" },
  MX: { name: "Mexico", continent: "North America" },
  MY: { name: "Malaysia", continent: "Asia" },
  MZ: { name: "Mozambique", continent: "Africa" },
  NA: { name: "Namibia", continent: "Africa" },
  NC: { name: "New Caledonia", continent: "Oceania" },
  NE: { name: "Niger", continent: "Africa" },
  NF: { name: "Norfolk Island", continent: "Oceania" },
  NG: { name: "Nigeria", continent: "Africa" },
  NI: { name: "Nicaragua", continent: "North America" },
  NL: { name: "Netherlands", continent: "Europe" },
  NO: { name: "Norway", continent: "Europe" },
  NP: { name: "Nepal", continent: "Asia" },
  NR: { name: "Nauru", continent: "Oceania" },
  NU: { name: "Niue", continent: "Oceania" },
  NZ: { name: "New Zealand", continent: "Oceania" },
  OM: { name: "Oman", continent: "Asia" },
  PA: { name: "Panama", continent: "North America" },
  PE: { name: "Peru", continent: "South America" },
  PF: { name: "French Polynesia", continent: "Oceania" },
  PG: { name: "Papua New Guinea", continent: "Oceania" },
  PH: { name: "Philippines", continent: "Asia" },
  PK: { name: "Pakistan", continent: "Asia" },
  PL: { name: "Poland", continent: "Europe" },
  PM: { name: "Saint Pierre and Miquelon", continent: "North America" },
  PN: { name: "Pitcairn Islands", continent: "Oceania" },
  PR: { name: "Puerto Rico", continent: "North America" },
  PS: { name: "Palestine", continent: "Asia" },
  PT: { name: "Portugal", continent: "Europe" },
  PW: { name: "Palau", continent: "Oceania" },
  PY: { name: "Paraguay", continent: "South America" },
  QA: { name: "Qatar", continent: "Asia" },
  RE: { name: "Réunion", continent: "Africa" },
  RO: { name: "Romania", continent: "Europe" },
  RS: { name: "Serbia", continent: "Europe" },
  RU: { name: "Russia", continent: "Europe" },
  RW: { name: "Rwanda", continent: "Africa" },
  SA: { name: "Saudi Arabia", continent: "Asia" },
  SB: { name: "Solomon Islands", continent: "Oceania" },
  SC: { name: "Seychelles", continent: "Africa" },
  SD: { name: "Sudan", continent: "Africa" },
  SE: { name: "Sweden", continent: "Europe" },
  SG: { name: "Singapore", continent: "Asia" },
  SH: { name: "Saint Helena", continent: "Africa" },
  SI: { name: "Slovenia", continent: "Europe" },
  SJ: { name: "Svalbard and Jan Mayen", continent: "Europe" },
  SK: { name: "Slovakia", continent: "Europe" },
  SL: { name: "Sierra Leone", continent: "Africa" },
  SM: { name: "San Marino", continent: "Europe" },
  SN: { name: "Senegal", continent: "Africa" },
  SO: { name: "Somalia", continent: "Africa" },
  SR: { name: "Suriname", continent: "South America" },
  SS: { name: "South Sudan", continent: "Africa" },
  ST: { name: "São Tomé and Príncipe", continent: "Africa" },
  SV: { name: "El Salvador", continent: "North America" },
  SX: { name: "Sint Maarten", continent: "North America" },
  SY: { name: "Syria", continent: "Asia" },
  SZ: { name: "Eswatini", continent: "Africa" },
  TC: { name: "Turks and Caicos Islands", continent: "North America" },
  TD: { name: "Chad", continent: "Africa" },
  TF: { name: "French Southern Territories", continent: "Antarctica" },
  TG: { name: "Togo", continent: "Africa" },
  TH: { name: "Thailand", continent: "Asia" },
  TJ: { name: "Tajikistan", continent: "Asia" },
  TK: { name: "Tokelau", continent: "Oceania" },
  TL: { name: "Timor-Leste", continent: "Asia" },
  TM: { name: "Turkmenistan", continent: "Asia" },
  TN: { name: "Tunisia", continent: "Africa" },
  TO: { name: "Tonga", continent: "Oceania" },
  TR: { name: "Turkey", continent: "Asia" },
  TT: { name: "Trinidad and Tobago", continent: "North America" },
  TV: { name: "Tuvalu", continent: "Oceania" },
  TW: { name: "Taiwan", continent: "Asia" },
  TZ: { name: "Tanzania", continent: "Africa" },
  UA: { name: "Ukraine", continent: "Europe" },
  UG: { name: "Uganda", continent: "Africa" },
  UM: { name: "U.S. Minor Outlying Islands", continent: "Oceania" },
  US: { name: "United States", continent: "North America" },
  UY: { name: "Uruguay", continent: "South America" },
  UZ: { name: "Uzbekistan", continent: "Asia" },
  VA: { name: "Vatican City", continent: "Europe" },
  VC: { name: "Saint Vincent and the Grenadines", continent: "North America" },
  VE: { name: "Venezuela", continent: "South America" },
  VG: { name: "British Virgin Islands", continent: "North America" },
  VI: { name: "U.S. Virgin Islands", continent: "North America" },
  VN: { name: "Vietnam", continent: "Asia" },
  VU: { name: "Vanuatu", continent: "Oceania" },
  WF: { name: "Wallis and Futuna", continent: "Oceania" },
  WS: { name: "Samoa", continent: "Oceania" },
  YE: { name: "Yemen", continent: "Asia" },
  YT: { name: "Mayotte", continent: "Africa" },
  ZA: { name: "South Africa", continent: "Africa" },
  ZM: { name: "Zambia", continent: "Africa" },
  ZW: { name: "Zimbabwe", continent: "Africa" },
};

/**
 * Get a country info by code. Returns undefined if not found.
 */
export function getCountry(code: string): CountryInfo | undefined {
  return COUNTRIES[code.toUpperCase()];
}

/**
 * UN M49 numeric code → ISO 3166-1 alpha-2 code mapping.
 * Used to translate world-atlas feature IDs (M49) to travel data codes (alpha-2).
 * Covers all 177 countries in world-atlas 110m resolution.
 */
const M49_TO_ALPHA2: Record<string, string> = {
  "004": "AF", "008": "AL", "012": "DZ", "024": "AO", "031": "AZ", "032": "AR",
  "036": "AU", "040": "AT", "050": "BD", "051": "AM", "056": "BE", "064": "BT",
  "068": "BO", "070": "BA", "072": "BW", "076": "BR", "084": "BZ", "090": "SB",
  "096": "BN", "100": "BG", "104": "MM", "108": "BI", "116": "KH", "120": "CM",
  "124": "CA", "140": "CF", "144": "LK", "148": "TD", "152": "CL", "156": "CN",
  "170": "CO", "174": "KM", "178": "CG", "180": "CD", "188": "CR", "191": "HR",
  "192": "CU", "196": "CY", "203": "CZ", "204": "BJ", "208": "DK", "212": "DM",
  "214": "DO", "218": "EC", "222": "SV", "226": "GQ", "231": "ET", "232": "ER",
  "233": "EE", "242": "FJ", "246": "FI", "250": "FR", "266": "GA", "268": "GM",
  "270": "GE", "275": "PS", "276": "DE", "288": "GH", "292": "GI", "300": "GR",
  "304": "GL", "308": "GD", "316": "GU", "320": "GT", "324": "GN", "328": "GY",
  "332": "HT", "340": "HN", "348": "HU", "352": "IS", "356": "IN", "360": "ID",
  "364": "IR", "368": "IQ", "372": "IE", "376": "IL", "380": "IT", "384": "CI",
  "388": "JM", "392": "JP", "398": "KZ", "400": "JO", "404": "KE", "408": "KP",
  "410": "KR", "414": "KW", "417": "KG", "418": "LA", "422": "LB", "426": "LS",
  "428": "LV", "430": "LR", "434": "LY", "440": "LT", "442": "LU", "450": "MG",
  "454": "MW", "458": "MY", "466": "ML", "470": "MT", "478": "MA", "484": "MX",
  "496": "MN", "498": "MD", "499": "ME", "504": "MA", "508": "MO", "512": "OM",
  "516": "EH", "524": "NP", "528": "NL", "540": "NC", "548": "VU", "554": "NZ",
  "558": "NI", "562": "NE", "566": "NG", "570": "NA", "578": "NO", "586": "PK",
  "591": "PA", "598": "PG", "600": "PY", "604": "PE", "608": "PH", "616": "PL",
  "620": "PT", "624": "GW", "626": "TL", "630": "PR", "634": "QA", "638": "RE",
  "642": "RO", "643": "RU", "646": "RW", "652": "KY", "654": "SH", "660": "WS",
  "670": "AS", "674": "SM", "678": "ST", "682": "SA", "686": "SN", "688": "RS",
  "690": "SC", "694": "SL", "702": "SG", "703": "SK", "704": "VN", "705": "SI",
  "706": "SO", "710": "ZA", "716": "ZW", "724": "ES", "728": "SS", "729": "SD",
  "740": "SR", "748": "SZ", "752": "SE", "756": "CH", "760": "SY", "762": "TJ",
  "764": "TH", "768": "TG", "772": "TO", "776": "TT", "780": "TR", "784": "AE",
  "788": "TN", "792": "TW", "795": "TM", "800": "UG", "804": "UA", "807": "MK",
  "818": "EG", "826": "GB", "834": "TZ", "840": "US", "854": "BF", "858": "UY",
  "860": "UZ", "862": "VE", "887": "YE", "894": "ZM", "900": "XK",
};

/**
 * Convert a UN M49 numeric code to ISO 3166-1 alpha-2 code.
 * Returns undefined if the M49 code is not found in the mapping.
 */
export function m49ToAlpha2(m49Code: string): string | undefined {
  return M49_TO_ALPHA2[m49Code];
}

/**
 * Get all countries sorted by name.
 */
export function getAllCountriesSorted(): { value: string; label: string; continent: string }[] {
  return Object.entries(COUNTRIES)
    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
    .map(([code, info]) => ({
      value: code,
      label: `<i class="fi fi-${code.toLowerCase()}"></i> ${info.name}`,
      continent: info.continent,
    }));
}
